import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Home from './pages/Home';
import Login from './pages/Login';
import Splash from './pages/Splash';
import Activate from './pages/Activate';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Dashboard from './pages/Dashboard';
import Admin from './pages/Admin';
import CleanerDashboard from './pages/CleanerDashboard';
import Contact from './pages/Contact';
import Pricing from './pages/Pricing';
import About from './pages/About';
import FAQ from './pages/FAQ';
import AppDownload from './pages/AppDownload';
import Privacy from './pages/Privacy';
import Terms from './pages/Terms';
import TermsVerhuurder from './pages/TermsVerhuurder';
import TermsSchoonmaker from './pages/TermsSchoonmaker';
import DemoDeviceFrame from './pages/DemoDeviceFrame';
import ImpersonateLanding from './pages/ImpersonateLanding';
import { getAuthToken, getAuthUser } from './utils/authSession';
import useModalScrollLock from './utils/useModalScrollLock';
import ErrorBoundary from './components/ErrorBoundary';
import './App.css';

// Kleine helper voor de hernoemde routes (/pricing -> /selection, /app ->
// /download): bestaande/gedeelde links (bv. al ergens extern gedeeld, of
// nog geindexeerd door Google) blijven zo werken i.p.v. een 404 te geven,
// en query-parameters (bv. ?tab=schoonmaak) blijven daarbij ook behouden.
function LegacyRedirect({ to }) {
  const location = useLocation();
  return <Navigate to={`${to}${location.search}`} replace />;
}

// Zonder dit bleef een nieuwe pagina na het klikken op bv. een tegel op de
// homepage gewoon op de scrollpositie staan waar je op de vorige pagina was
// (React Router behoudt de scroll niet zelf, en de browser maakt dat bij een
// SPA-navigatie ook niet automatisch ongedaan). Springt nu bij elke routewissel
// (nieuw pathname) terug naar boven, net zoals een "echte" nieuwe
// paginalaad dat zou doen. Gebruikt pathname i.p.v. de volledige location
// zodat enkel wisselen van query-string (bv. ?tab=...) niet ook telkens
// terugspringt.
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

// De app was tot voor kort app-breed vergrendeld op portretmodus (zie
// git-historiek in AndroidManifest.xml/Info.plist), omdat de native WebView
// (vooral WKWebView op iOS, soms ook Android) na een rotatie de layout niet
// altijd meteen correct herschilderde: oude, al getekende tekst bleef op zijn
// vorige positie staan terwijl de nieuw herberekende content eroverheen
// getekend werd, met overlappende/dubbel getekende tekst tot gevolg. Nu
// rotatie weer is toegestaan, lossen we die onderliggende oorzaak hier op
// i.p.v. per pagina: een korte, onzichtbare opacity-flikker vlak na
// "orientationchange"/"resize" dwingt de WebView tot een volledige repaint
// van de hele pagina, zonder scrollpositie of een actief invoerveld (focus)
// te verstoren - in tegenstelling tot bv. tijdelijk display:none zetten.
function OrientationRepaintFix() {
  useEffect(() => {
    let timeoutId;
    const forceRepaint = () => {
      clearTimeout(timeoutId);
      // Kleine vertraging: net na "orientationchange" geeft iOS de nieuwe
      // window-afmetingen soms nog niet meteen door.
      timeoutId = setTimeout(() => {
        document.body.style.opacity = '0.999';
        requestAnimationFrame(() => {
          document.body.style.opacity = '';
        });
      }, 150);
    };

    window.addEventListener('orientationchange', forceRepaint);
    window.addEventListener('resize', forceRepaint);
    return () => {
      clearTimeout(timeoutId);
      window.removeEventListener('orientationchange', forceRepaint);
      window.removeEventListener('resize', forceRepaint);
    };
  }, []);

  return null;
}

// Vangnet voor de hele pagina: bij een onverwachte fout blijft er nooit een leeg
// scherm over, en navigeren naar een andere route wist de fout meteen.
function RouteErrorBoundary({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}>{children}</ErrorBoundary>;
}

function App() {
  // Pagina achter een open pop-up blijft stilstaan (zie utils/useModalScrollLock.js).
  useModalScrollLock();

  // Houdt de CSS-variabele --nav-bottom (onderkant van de sticky navbar) up to
  // date, zodat de zijpanelen (hamburger/meldingen) exact tegen de navbar
  // aansluiten, ongeacht de werkelijke hoogte ervan.
  useEffect(() => {
    // Schrijft enkel als de waarde echt veranderd is: elke schrijfactie op <html>
    // laat de browser de héle pagina opnieuw berekenen, en dat elke 300ms
    // tijdens het scrollen kan de scroll op iOS storen.
    let last = '';
    const update = () => {
      const nav = document.querySelector('.cleaner-nav, .dashboard-nav, .admin-nav');
      if (nav) {
        const value = `${Math.round(nav.getBoundingClientRect().bottom)}px`;
        if (value !== last) {
          last = value;
          document.documentElement.style.setProperty('--nav-bottom', value);
        }
      }
    };
    update();
    const id = setInterval(update, 500);
    window.addEventListener('resize', update);
    return () => { clearInterval(id); window.removeEventListener('resize', update); };
  }, []);

  const isNativeMobileApp = () => {
    // Capacitor injecht dit globaal object in de native app-shell; dit is de
    // betrouwbaarste check zodra de app effectief als Capacitor-app draait.
    if (window.Capacitor && typeof window.Capacitor.isNativePlatform === 'function') {
      return window.Capacitor.isNativePlatform();
    }

    const userAgent = navigator.userAgent.toLowerCase();
    return userAgent.includes('capacitor') ||
           userAgent.includes('cordova') ||
           userAgent.includes('smartstaysapp');
  };

  // Bepaal dit synchroon tijdens het renderen (niet in een useEffect) en
  // schrijf het meteen weg. Op een verse app-installatie bestaat
  // "isNativeApp" nog niet in localStorage; als dat pas in een useEffect
  // gezet wordt, is de allereerste render (en dus de routing hieronder,
  // en ook alle child-pagina's die zelf localStorage.isNativeApp uitlezen)
  // nog fout - de app toonde dan bij de eerste opstart de webversie (Home)
  // in plaats van meteen het splashscreen/login te tonen. Synchroon bepalen
  // en wegschrijven, vóór er iets anders rendert, lost dat op.
  const urlNative = new URLSearchParams(window.location.search).get('native');
  const isNative = urlNative === 'true' ? true : urlNative === 'false' ? false : isNativeMobileApp();
  localStorage.setItem('isNativeApp', isNative ? 'true' : 'false');

  const isAuthenticated = () => {
    return getAuthToken() !== null;
  };

  const getUserRole = () => {
    const user = getAuthUser();
    if (user) {
      try {
        return JSON.parse(user).role;
      } catch (e) {
        return null;
      }
    }
    return null;
  };

  return (
    <Router>
      <ScrollToTop />
      <OrientationRepaintFix />
      <RouteErrorBoundary>
      <Routes>
        {/* Webapp routes */}
        {!isNative && (
          <>
            <Route path="/" element={<Home />} />
            {/* Splash bestaat niet in webapp, redirect naar home */}
            <Route path="/splash" element={<Navigate to="/" />} />
          </>
        )}
        
        {/* Native app routes */}
        {isNative && (
          <>
            <Route path="/" element={<Splash />} />
            <Route path="/splash" element={<Splash />} />
            {/* Home bestaat niet in native app, redirect naar splash */}
            <Route path="/home" element={<Navigate to="/" />} />
          </>
        )}
        
        {/* Gedeelde routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/selection" element={<Pricing />} />
        <Route path="/about" element={<About />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/download" element={<AppDownload />} />
        <Route path="/privacy" element={<Privacy />} />
        {/* Publieke website: enkel de voorwaarden voor verhuurders. De
            voorwaarden voor schoonmakers zijn alleen zichtbaar voor een
            ingelogde schoonmaker (via het profielmenu), niet voor bezoekers. */}
        <Route path="/terms" element={<Navigate to="/terms/verhuurders" replace />} />
        <Route path="/terms/verhuurders" element={<TermsVerhuurder />} />
        <Route
          path="/terms/schoonmakers"
          element={
            isAuthenticated() && getUserRole() === 'SCHOONMAKER'
              ? <TermsSchoonmaker />
              : <Navigate to="/terms/verhuurders" replace />
          }
        />
        {/* Oude routenamen: /pricing en /app waren geen goede vertaling/naam
            (zie Navbar/Home - "Aanbod" is geen "pricing", en de downloadpagina
            heette al overal "Download"). Blijven bestaan als redirect. */}
        <Route path="/pricing" element={<LegacyRedirect to="/selection" />} />
        <Route path="/app" element={<LegacyRedirect to="/download" />} />
        <Route path="/activate/:token" element={<Activate />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        {/* Verkoopdemo: geen login nodig, volledig losstaand van echte data -
            instellingen (welke abonnementen) komen mee via de querystring,
            zie Admin.js's "Demo"-tab. */}
        <Route path="/demo" element={<DemoDeviceFrame />} />
        {/* Bruggetje voor "Inloggen als" vanuit Admin.js (Gebruikers-tab):
            zie ImpersonateLanding.js voor waarom dit geen normale, beveiligde
            route is. */}
        <Route path="/impersonate" element={<ImpersonateLanding />} />
        <Route 
          path="/dashboard" 
          element={
            isAuthenticated() && getUserRole() === 'VERHUURDER' ? 
            <Dashboard /> : 
            <Navigate to={isNative ? "/" : "/login"} />
          } 
        />
        <Route 
          path="/cleaner" 
          element={
            isAuthenticated() && getUserRole() === 'SCHOONMAKER' ? 
            <CleanerDashboard /> : 
            <Navigate to={isNative ? "/" : "/login"} />
          } 
        />
        <Route 
          path="/admin" 
          element={
            isAuthenticated() && getUserRole() === 'ADMIN' ? 
            <Admin /> : 
            <Navigate to={isNative ? "/" : "/login"} />
          } 
        />
      </Routes>
      </RouteErrorBoundary>
    </Router>
  );
}

export default App;